import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { getProjectConcretePours } from "../../../services/api/dispatch";
import {
  getJobDocumentFolders,
  getJobFolderPath,
} from "../../../services/api/files";
import { getProject } from "../../../services/api/projects";
import { getRFIs } from "../../../services/api/rfi";
import { getProjectPayoutHours } from "../../../services/api/timekeeping";
import { getTMLogs } from "../../../services/api/tmlog";
import {
  DEFAULT_PROJECT_TAB,
  EMPTY_TICKET_SUMMARY,
  PROJECT_PAGE_PLACEHOLDER_CONTENT,
} from "../constants";
import { isTicketForProject } from "../shared/projectTicketSummaries";
import {
  buildDocumentFolderLinkPlaceholders,
  getEmptyDocumentFoldersState,
} from "./projectDocumentFolders";
import { buildProjectMonthOptions } from "../../../utils/business/jobs";
import {
  aggregateConcretePoursEntries,
  aggregateWorkerHoursEntries,
  buildConcretePoursMetrics,
  buildWorkerHoursMetrics,
  getDefaultOverviewMetricsSelection,
  getLoadingConcretePoursMetrics,
  getLoadingWorkerHoursMetrics,
  OVERVIEW_METRICS_TYPE_ALL,
  selectionFromRangeKey,
} from "./metrics/metricsHelpers";

const ProjectPageContext = createContext(null);

/**
 * Whether an Axios failure looks like a dropped connection (dev reload / blip).
 *
 * @param {unknown} error
 * @returns {boolean}
 */
function isTransientNetworkError(error) {
  const code = error?.code;
  const message = String(error?.message || "");
  return (
    code === "ERR_NETWORK" ||
    code === "ECONNABORTED" ||
    message === "Network Error" ||
    message.includes("ERR_INVALID_HTTP_RESPONSE")
  );
}

/**
 * Run an async loader once, retrying once after a short delay on network blips.
 *
 * @template T
 * @param {() => Promise<T>} loader
 * @param {AbortSignal} [signal] - Skip retry when the request was cancelled.
 * @returns {Promise<T>}
 */
async function loadWithNetworkRetry(loader, signal) {
  try {
    return await loader();
  } catch (error) {
    if (
      signal?.aborted ||
      error?.code === "ERR_CANCELED" ||
      !isTransientNetworkError(error)
    ) {
      throw error;
    }
    await new Promise((resolve) => {
      window.setTimeout(resolve, 350);
    });
    if (signal?.aborted) {
      throw error;
    }
    return loader();
  }
}

/**
 * Return the empty/loading summary shape used by project ticket cards.
 *
 * @param {boolean} isLoading - Whether the summary is loading.
 * @returns {Object}
 */
function getEmptyTicketSummary(isLoading = false) {
  return {
    items: [],
    isLoading,
    error: null,
  };
}

/**
 * Read the current ProjectPage context value.
 *
 * @returns {Object}
 */
export function useProjectPageContext() {
  const context = useContext(ProjectPageContext);
  if (!context) {
    throw new Error("useProjectPageContext must be used within ProjectPageProvider");
  }
  return context;
}

/**
 * Provide shared state for the project detail page and its tabs.
 *
 * @param {Object} props - Component props.
 * @param {string|number} props.projectId - Backing JobNumber primary key.
 * @param {React.ReactNode} props.children - Child page content.
 * @returns {JSX.Element}
 */
export const ProjectPageProvider = ({ projectId, children }) => {
  const [project, setProject] = useState(null);
  const [activeTab, setActiveTab] = useState(DEFAULT_PROJECT_TAB);
  const [editingProject, setEditingProject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tmTicketSummary, setTmTicketSummary] = useState(EMPTY_TICKET_SUMMARY);
  const [rfiTicketSummary, setRfiTicketSummary] = useState(EMPTY_TICKET_SUMMARY);
  // Curated Egnyte document folders — loaded once per project (keep-alive safe).
  const [documentFolders, setDocumentFolders] = useState(
    getEmptyDocumentFoldersState
  );
  // Full project metrics payloads — loaded once; Type/Range filter client-side.
  const [workerHoursSource, setWorkerHoursSource] = useState(null);
  const [workerHoursLoading, setWorkerHoursLoading] = useState(false);
  const [workerHoursError, setWorkerHoursError] = useState(null);
  const [workerHoursRange, setWorkerHoursRange] = useState(
    getDefaultOverviewMetricsSelection
  );
  const [workerHoursType, setWorkerHoursType] = useState(
    OVERVIEW_METRICS_TYPE_ALL
  );
  const [concretePoursSource, setConcretePoursSource] = useState(null);
  const [concretePoursLoading, setConcretePoursLoading] = useState(false);
  const [concretePoursError, setConcretePoursError] = useState(null);
  const [concretePoursRange, setConcretePoursRange] = useState(
    getDefaultOverviewMetricsSelection
  );
  const [concretePoursType, setConcretePoursType] = useState(
    OVERVIEW_METRICS_TYPE_ALL
  );

  const overviewMetricsMonthOptions = useMemo(
    () => buildProjectMonthOptions(project),
    [project]
  );

  const setWorkerHoursRangeKey = useCallback((key) => {
    setWorkerHoursRange(selectionFromRangeKey(key));
  }, []);

  const setConcretePoursRangeKey = useCallback((key) => {
    setConcretePoursRange(selectionFromRangeKey(key));
  }, []);

  const setWorkerHoursTypeKey = useCallback((key) => {
    setWorkerHoursType(key || OVERVIEW_METRICS_TYPE_ALL);
  }, []);

  const setConcretePoursTypeKey = useCallback((key) => {
    setConcretePoursType(key || OVERVIEW_METRICS_TYPE_ALL);
  }, []);

  // Derive tile metrics from cached source + current filters (no refetch).
  const workerHoursSummary = useMemo(() => {
    const entries = workerHoursSource?.entries || [];
    const workerTypes = workerHoursSource?.workerTypes || [];
    if (workerHoursLoading) {
      return {
        data: null,
        metrics: getLoadingWorkerHoursMetrics(workerHoursType, workerTypes),
        isLoading: true,
        error: null,
        range: workerHoursRange,
        type: workerHoursType,
        workerTypes,
        entries,
      };
    }
    const data = aggregateWorkerHoursEntries(
      entries,
      workerHoursRange,
      workerHoursType
    );
    return {
      data,
      metrics: buildWorkerHoursMetrics(data, workerHoursType, workerTypes),
      isLoading: false,
      error: workerHoursError,
      range: workerHoursRange,
      type: workerHoursType,
      workerTypes,
      entries,
    };
  }, [
    workerHoursSource,
    workerHoursLoading,
    workerHoursError,
    workerHoursRange,
    workerHoursType,
  ]);

  const concretePoursSummary = useMemo(() => {
    const entries = concretePoursSource?.entries || [];
    if (concretePoursLoading) {
      return {
        data: null,
        metrics: getLoadingConcretePoursMetrics(concretePoursType),
        isLoading: true,
        error: null,
        range: concretePoursRange,
        type: concretePoursType,
        entries,
      };
    }
    const data = aggregateConcretePoursEntries(
      entries,
      concretePoursRange,
      concretePoursType
    );
    return {
      data,
      metrics: buildConcretePoursMetrics(data, concretePoursType),
      isLoading: false,
      error: concretePoursError,
      range: concretePoursRange,
      type: concretePoursType,
      entries,
    };
  }, [
    concretePoursSource,
    concretePoursLoading,
    concretePoursError,
    concretePoursRange,
    concretePoursType,
  ]);

  const refreshProject = useCallback(() => {
    setIsLoading(true);
    setError(null);
    return getProject(projectId)
      .then(setProject)
      .catch((requestError) => {
        console.error("Failed to load project", requestError);
        setError("Project could not be loaded. Please return to the project list.");
      })
      .finally(() => setIsLoading(false));
  }, [projectId]);

  useEffect(() => {
    refreshProject();
  }, [refreshProject]);

  useEffect(() => {
    if (!project) {
      setTmTicketSummary(getEmptyTicketSummary());
      setRfiTicketSummary(getEmptyTicketSummary());
      setDocumentFolders(getEmptyDocumentFoldersState());
      return undefined;
    }

    let isCancelled = false;
    setTmTicketSummary(getEmptyTicketSummary(true));
    setRfiTicketSummary(getEmptyTicketSummary(true));

    Promise.all([getTMLogs(), getRFIs()])
      .then(([tmLogs, rfis]) => {
        if (isCancelled) return;
        setTmTicketSummary({
          items: tmLogs.filter((ticket) => isTicketForProject(ticket, project)),
          isLoading: false,
          error: null,
        });
        setRfiTicketSummary({
          items: rfis.filter((ticket) => isTicketForProject(ticket, project)),
          isLoading: false,
          error: null,
        });
      })
      .catch((requestError) => {
        if (isCancelled) return;
        console.error("Failed to load project ticket summaries", requestError);
        const summaryError = "Ticket summaries could not be loaded.";
        setTmTicketSummary({
          items: [],
          isLoading: false,
          error: summaryError,
        });
        setRfiTicketSummary({
          items: [],
          isLoading: false,
          error: summaryError,
        });
      });

    return () => {
      isCancelled = true;
    };
  }, [project]);

  // Resolve Egnyte job path ASAP for links, then load throttled folder stats once.
  useEffect(() => {
    if (!project?.number) {
      setDocumentFolders(getEmptyDocumentFoldersState());
      return undefined;
    }

    let isCancelled = false;
    setDocumentFolders(getEmptyDocumentFoldersState(true));

    getJobFolderPath(project.number)
      .then((pathPayload) => {
        if (isCancelled) return null;
        if (!pathPayload?.path) {
          setDocumentFolders({
            ...getEmptyDocumentFoldersState(),
            error: "Project folder could not be resolved.",
          });
          return null;
        }

        setDocumentFolders({
          path: pathPayload.path,
          webUrl: pathPayload.webUrl || null,
          folders: buildDocumentFolderLinkPlaceholders(pathPayload.path),
          isLoading: false,
          statsLoading: true,
          error: null,
        });

        return getJobDocumentFolders({ folderPath: pathPayload.path });
      })
      .then((foldersPayload) => {
        if (isCancelled) return;
        if (!foldersPayload?.folders) {
          setDocumentFolders((prev) => ({
            ...prev,
            isLoading: false,
            statsLoading: false,
          }));
          return;
        }
        setDocumentFolders((prev) => ({
          path: foldersPayload.path || prev.path,
          webUrl: foldersPayload.webUrl || prev.webUrl,
          folders: foldersPayload.folders,
          isLoading: false,
          statsLoading: false,
          error: null,
        }));
      })
      .catch((requestError) => {
        if (isCancelled) return;
        console.error("Failed to load project document folders", requestError);
        setDocumentFolders((prev) => ({
          ...prev,
          isLoading: false,
          statsLoading: false,
          error: prev.folders?.length
            ? null
            : "Project files could not be loaded.",
        }));
      });

    return () => {
      isCancelled = true;
    };
  }, [project?.number]);

  // Load full metrics payloads once per project (filters are client-side).
  useEffect(() => {
    if (!project?.id) {
      setWorkerHoursSource(null);
      setWorkerHoursError(null);
      setWorkerHoursLoading(false);
      setConcretePoursSource(null);
      setConcretePoursError(null);
      setConcretePoursLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    const { signal } = controller;
    setWorkerHoursLoading(true);
    setWorkerHoursError(null);
    setConcretePoursLoading(true);
    setConcretePoursError(null);

    loadWithNetworkRetry(
      () => getProjectPayoutHours({ job: project.id }, { signal }),
      signal
    )
      .then((data) => {
        if (signal.aborted) return;
        setWorkerHoursSource(data);
        setWorkerHoursLoading(false);
      })
      .catch((requestError) => {
        if (signal.aborted || requestError?.code === "ERR_CANCELED") return;
        console.error("Failed to load project payout hours", requestError);
        setWorkerHoursSource(null);
        setWorkerHoursError("Worker hours could not be loaded.");
        setWorkerHoursLoading(false);
      });

    loadWithNetworkRetry(
      () => getProjectConcretePours({ job: project.id }, { signal }),
      signal
    )
      .then((data) => {
        if (signal.aborted) return;
        setConcretePoursSource(data);
        setConcretePoursLoading(false);
      })
      .catch((requestError) => {
        if (signal.aborted || requestError?.code === "ERR_CANCELED") return;
        console.error("Failed to load project concrete pours", requestError);
        setConcretePoursSource(null);
        setConcretePoursError("Concrete pours could not be loaded.");
        setConcretePoursLoading(false);
      });

    return () => {
      controller.abort();
    };
  }, [project?.id]);

  const openEditProject = useCallback(() => {
    setEditingProject(project);
  }, [project]);

  const closeEditProject = useCallback(() => {
    setEditingProject(null);
  }, []);

  const handleProjectSaved = useCallback(() => {
    setEditingProject(null);
    refreshProject();
  }, [refreshProject]);

  const value = useMemo(
    () => ({
      project,
      projectId,
      activeTab,
      setActiveTab,
      editingProject,
      openEditProject,
      closeEditProject,
      handleProjectSaved,
      isLoading,
      error,
      refreshProject,
      placeholderContent: PROJECT_PAGE_PLACEHOLDER_CONTENT,
      tmTicketSummary,
      setTmTicketSummary,
      rfiTicketSummary,
      setRfiTicketSummary,
      documentFolders,
      workerHoursSummary,
      workerHoursRange,
      setWorkerHoursRangeKey,
      setWorkerHoursTypeKey,
      concretePoursSummary,
      concretePoursRange,
      setConcretePoursRangeKey,
      setConcretePoursTypeKey,
      overviewMetricsMonthOptions,
    }),
    [
      project,
      projectId,
      activeTab,
      editingProject,
      openEditProject,
      closeEditProject,
      handleProjectSaved,
      isLoading,
      error,
      refreshProject,
      tmTicketSummary,
      rfiTicketSummary,
      documentFolders,
      workerHoursSummary,
      workerHoursRange,
      setWorkerHoursRangeKey,
      setWorkerHoursTypeKey,
      concretePoursSummary,
      concretePoursRange,
      setConcretePoursRangeKey,
      setConcretePoursTypeKey,
      overviewMetricsMonthOptions,
    ]
  );

  return (
    <ProjectPageContext.Provider value={value}>
      {children}
    </ProjectPageContext.Provider>
  );
};

export default ProjectPageContext;
